/**
 * Cardsphere has no static bulk export url like the other vendors - the card list has to be posted
 * to our api, which hands back a one-off wishlist url to open.
 *
 * Each card is a line that may lead with a quantity, e.g. `10 Forest`, or be a bare name for a
 * single copy.
 */
import { useCallback, useState } from 'react'

import { useReduxDispatch } from '@/store/hooks'
import { toastActions } from '@/store/toast'
import { parseError } from '@/utils/commonUtil'
import { fetchApi } from '@/utils/requestUtil'

type Props = {
  decklist: string[]
  medium: string
  onTrack?: (label: string) => void
}

export const useCardsphereExport = ({ decklist, medium, onTrack }: Props) => {
  const dispatch = useReduxDispatch()
  const [loading, setLoading] = useState(false)

  const onClick = useCallback(() => {
    if (loading || decklist.length === 0) return
    setLoading(true)

    void (async () => {
      try {
        const url = (
          await fetchApi<{ url?: string }>({
            base: 'api',
            options: {
              body: JSON.stringify({ cards: decklist }),
              headers: { accept: 'application/json', 'content-type': 'application/json' },
              method: 'post',
            },
            path: '/cardsphere',
          })
        ).data.url

        if (!url) throw new Error('URL not found')

        const outboundUrl = new URL(url)
        outboundUrl.searchParams.set('referrer', `edhrec-${medium}`)
        const href = outboundUrl.toString()
        window.open(href, '_blank')
        onTrack?.(href)
      } catch (e) {
        dispatch(
          toastActions.add(
            <>
              <strong>Cardsphere Export Error:</strong> {parseError(e, 'Cardsphere Export error')}
            </>,
          ),
        )
      } finally {
        setLoading(false)
      }
    })()
  }, [dispatch, loading, medium, decklist, onTrack])

  return { loading, onClick }
}
